import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from 'url:../../assets-optimized/redesign/logo.png';
import logoavif from 'url:../../assets-optimized/redesign/logo.avif';
import logowebp from 'url:../../assets-optimized/redesign/logo.webp';
import OptimizedImage from '../Redesign/OptimizedImage';
import ContactModal from '../Redesign/ContactModal';
import './Header.css';

const Header = ({ hideContactButton = false }) => {
  const location = useLocation();
  const isContactPage = location.pathname === '/contact';
  const isHomePage = location.pathname === '/';

  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuClosing, setMobileMenuClosing] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Contact actions are always visible now (the redesigned hero shows them over the photo).
  const showScrolledContact = true;
  const closeTimerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuToggleRef = useRef(null);
  const mobileMenuCloseRef = useRef(null);

  // On the home page the header overlays the hero photo (transparent, light text)
  // until the hero scrolls past, at which point it becomes the solid bar.
  const [heroOverlayActive, setHeroOverlayActive] = useState(isHomePage);

  const MOBILE_MENU_CLOSE_DURATION = 280;
  const desktopSolutionsMenuId = 'desktop-solutions-menu';
  const mobileSolutionsMenuId = 'mobile-solutions-menu';

  useEffect(() => {
    if (!isHomePage) {
      setHeroOverlayActive(false);
      return undefined;
    }

    const handleScroll = () => {
      const hero = document.querySelector('.hpc-hero');
      const heroBottom = hero ? hero.getBoundingClientRect().bottom : 0;
      // Keep the overlay while the hero photo is still behind the header.
      setHeroOverlayActive(heroBottom > 72);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const openMobileMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMobileMenuClosing(false);
    setMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    if (mobileMenuClosing) {
      return;
    }

    setMobileMenuClosing(true);

    closeTimerRef.current = setTimeout(() => {
      setMobileMenuOpen(false);
      setMobileMenuClosing(false);
      closeTimerRef.current = null;
      mobileMenuToggleRef.current?.focus();
    }, MOBILE_MENU_CLOSE_DURATION);
  };

  // Dialog focus trap: keep Tab/Shift+Tab cycling within the open mobile menu
  // and let Escape close it, per the WAI-ARIA dialog pattern.
  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const overlay = mobileMenuRef.current;
    if (!overlay) {
      return undefined;
    }

    const focusableSelector =
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    mobileMenuCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(overlay.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileMenuOpen]);

  return (
    <header className={`app-header ${heroOverlayActive ? 'app-header--overlay' : ''}`}>
      <div className={`header-container ${hideContactButton ? 'contact-hidden' : ''}`}>
        {/* Logo */}
        <Link to='/' className='header-logo-link'>
          <OptimizedImage
            avif={logoavif}
            webp={logowebp}
            fallback={logo}
            alt='Marriott Media'
            className='header-logo'
          />
        </Link>

        {/* Navigation */}
        <nav
          className={`header-nav ${hideContactButton || !showScrolledContact ? 'align-right' : ''}`}
        >
          <ul className='nav-list'>
            <li
              className={`nav-item dropdown ${solutionsOpen ? 'open' : ''}`}
              onMouseEnter={() => setSolutionsOpen(true)}
              onMouseLeave={() => setSolutionsOpen(false)}
              onFocus={() => setSolutionsOpen(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setSolutionsOpen(false);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setSolutionsOpen(false);
                }
              }}
            >
              <button
                type='button'
                className={`nav-link dropdown-toggle ${solutionsOpen ? 'open-state' : ''}`}
                aria-haspopup='true'
                aria-expanded={solutionsOpen}
                aria-controls={desktopSolutionsMenuId}
                onClick={() => setSolutionsOpen((current) => !current)}
              >
                SOLUTIONS
                <svg
                  className='dropdown-arrow'
                  width='10'
                  height='6'
                  viewBox='0 0 10 6'
                  fill='none'
                >
                  <path
                    d='M1 1L5 5L9 1'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
              <ul
                id={desktopSolutionsMenuId}
                className='dropdown-menu'
                aria-hidden={!solutionsOpen}
              >
                <li>
                  <NavLink
                    to='/solutions/on-property'
                    className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    On-property
                    <span className='dropdown-item-icon' aria-hidden='true'>
                      <svg viewBox='0 0 17 17' fill='none'>
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/solutions/marriott-digital'
                    className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    Marriott Digital
                    <span className='dropdown-item-icon' aria-hidden='true'>
                      <svg viewBox='0 0 17 17' fill='none'>
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/solutions/offsite-digital'
                    className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    Offsite Digital
                    <span className='dropdown-item-icon' aria-hidden='true'>
                      <svg viewBox='0 0 17 17' fill='none'>
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='currentColor'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </NavLink>
                </li>
              </ul>
            </li>
            <li className='nav-item'>
              <NavLink
                to='/partner-stories'
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                PARTNER STORIES
              </NavLink>
            </li>
            <li className='nav-item'>
              <NavLink
                to='/our-platform'
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                OUR PLATFORM
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* Right side actions */}
        <div className={`header-actions ${!showScrolledContact ? 'actions-hidden' : ''}`}>
          {!hideContactButton && (
            <>
              <button
                className={`contact-button ${!showScrolledContact ? 'button-hidden' : ''}`}
                onClick={() => setIsContactModalOpen(true)}
                disabled={isContactPage}
              >
                Contact Us
              </button>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className={`external-link ${!showScrolledContact ? 'button-hidden' : ''}`}
                aria-label='Contact us'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  aria-hidden='true'
                  focusable='false'
                >
                  <path d='M7 17L17 7M17 7H7M17 7V17' />
                </svg>
              </button>
            </>
          )}
        </div>

        <button
          type='button'
          ref={mobileMenuToggleRef}
          className='mobile-menu-toggle'
          onClick={openMobileMenu}
          aria-label='Open menu'
        >
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M4 7H20M4 12H20M4 17H20'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className={`mobile-menu-overlay ${mobileMenuClosing ? 'closing' : ''}`}
          role='dialog'
          aria-modal='true'
          aria-label='Mobile navigation menu'
          ref={mobileMenuRef}
        >
          <div className='mobile-menu-header'>
            <Link to='/' className='header-logo-link' onClick={closeMobileMenu}>
              <OptimizedImage
                avif={logoavif}
                webp={logowebp}
                fallback={logo}
                alt='Marriott Media'
                className='header-logo'
              />
            </Link>
            <button
              type='button'
              ref={mobileMenuCloseRef}
              className='mobile-menu-close'
              onClick={closeMobileMenu}
              aria-label='Close menu'
            >
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M6 6L18 18M18 6L6 18'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </button>
          </div>

          <div className='mobile-menu-body'>
            <div className={`mobile-solutions-card ${mobileSolutionsOpen ? 'open' : ''}`}>
              <button
                type='button'
                className='mobile-solutions-toggle'
                onClick={() => setMobileSolutionsOpen((current) => !current)}
                aria-expanded={mobileSolutionsOpen}
                aria-controls={mobileSolutionsMenuId}
              >
                SOLUTIONS
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <path
                    d={mobileSolutionsOpen ? 'M3 10L8 5L13 10' : 'M3 6L8 11L13 6'}
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>

              <ul
                id={mobileSolutionsMenuId}
                className={`mobile-solutions-list ${mobileSolutionsOpen ? 'open' : 'closed'}`}
                aria-hidden={!mobileSolutionsOpen}
              >
                <li>
                  <Link
                    to='/solutions/on-property'
                    className='mobile-solution-link'
                    onClick={closeMobileMenu}
                  >
                    ON-PROPERTY
                    <span className='mobile-solution-icon' aria-hidden='true'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='17'
                        height='17'
                        viewBox='0 0 17 17'
                        fill='none'
                      >
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to='/solutions/marriott-digital'
                    className='mobile-solution-link'
                    onClick={closeMobileMenu}
                  >
                    MARRIOTT DIGITAL
                    <span className='mobile-solution-icon' aria-hidden='true'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='17'
                        height='17'
                        viewBox='0 0 17 17'
                        fill='none'
                      >
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to='/solutions/offsite-digital'
                    className='mobile-solution-link'
                    onClick={closeMobileMenu}
                  >
                    OFFSITE DIGITAL
                    <span className='mobile-solution-icon' aria-hidden='true'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='17'
                        height='17'
                        viewBox='0 0 17 17'
                        fill='none'
                      >
                        <path
                          d='M5.30207 11.6637L11.4893 5.47656'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                          stroke='#1C1C1C'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className='mobile-primary-links'>
              <NavLink
                to='/partner-stories'
                className={({ isActive }) =>
                  `mobile-nav-card mobile-nav-case ${isActive ? 'active' : ''}`
                }
                onClick={closeMobileMenu}
              >
                PARTNER STORIES
              </NavLink>
              <NavLink
                to='/our-platform'
                className={({ isActive }) =>
                  `mobile-nav-card mobile-nav-platform ${isActive ? 'active' : ''}`
                }
                onClick={closeMobileMenu}
              >
                OUR PLATFORM
              </NavLink>
              {/* <NavLink
                to='/insights'
                className={({ isActive }) =>
                  `mobile-nav-card mobile-nav-insights ${isActive ? 'active' : ''}`
                }
                onClick={closeMobileMenu}
              >
                INSIGHTS
              </NavLink> */}
            </div>
          </div>

          <div className='mobile-menu-footer'>
            {!hideContactButton && (
              <div className={`mobile-contact-row ${!showScrolledContact ? 'actions-hidden' : ''}`}>
                {isContactPage ? (
                  <button className='mobile-contact-button' disabled>
                    Contact Us
                  </button>
                ) : (
                  <Link to='/contact' className='mobile-contact-button' onClick={closeMobileMenu}>
                    Contact Us
                  </Link>
                )}
                <a
                  href='https://www.marriott.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mobile-contact-icon'
                  aria-label='Visit Marriott (opens in a new tab)'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='34'
                    height='34'
                    viewBox='0 0 17 17'
                    fill='none'
                    aria-hidden='true'
                  >
                    <path
                      d='M5.30207 11.6637L11.4893 5.47656'
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </a>
              </div>
            )}

            <a
              href='https://www.linkedin.com/company/marriott-media/'
              target='_blank'
              rel='noopener noreferrer'
              className='mobile-linkedin-card'
            >
              <div>
                <p className='mobile-linkedin-title'>
                  FOLLOW US ON LINKEDIN
                  <span className='sr-only'> (opens in a new tab)</span>
                </p>
                <p className='mobile-linkedin-text'>
                  Get the latest on research, product launches, and how we’re shaping the future of
                  travel. Follow along on LinkedIn.
                </p>
              </div>
              <span className='mobile-linkedin-icon' aria-hidden='true'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='34'
                  height='34'
                  viewBox='0 0 17 17'
                  fill='none'
                >
                  <path
                    d='M5.30207 11.6637L11.4893 5.47656'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M11.6665 11.7882L11.6665 5.30228L4.78857 5.30227'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
            </a>
          </div>
        </div>
      )}

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </header>
  );
};

export default Header;
